import { supabase } from '../../../integrations/supabase/client';

export interface ProductKnowledgeBase {
  faq?: Array<{ question: string; answer: string }>;
  pricing?: string;
  nota?: string;  // Contraindicaciones/notas del producto
}

export interface AgentProduct {
  id?: string;
  flow_id: string;
  product_key: string;
  product_name: string;
  product_description: string | null;
  product_prompt: string | null;
  product_knowledge_base: string | null;
  user_id: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Genera un prompt formateado en markdown con todos los productos de un flow
 */
const generateProductsPrompt = async (flowId: string): Promise<string> => {
  // Obtener productos directamente de Supabase
  const { data: products, error } = await supabase
    .from('agent_products')
    .select('*')
    .eq('flow_id', flowId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  if (!products || products.length === 0) {
    return '';
  }

  let prompt = '**Productos:**\n\n';

  products.forEach((product, index) => {
    prompt += `${index + 1}. **${product.product_name}** (${product.product_key})\n`;

    if (product.product_description) {
      prompt += `   Descripción: ${product.product_description}\n`;
    }

    // Parse knowledge base if exists
    if (product.product_knowledge_base) {
      try {
        const kb = JSON.parse(product.product_knowledge_base) as ProductKnowledgeBase;

        if (kb.faq && kb.faq.length > 0) {
          prompt += `\n   Preguntas Frecuentes:\n`;
          kb.faq.forEach(item => {
            prompt += `   - ${item.question}\n     ${item.answer}\n`;
          });
        }

        if (kb.pricing) {
          prompt += `\n   Precios: ${kb.pricing}\n`;
        }

        if (kb.nota) {
          prompt += `\n   Nota: ${kb.nota}\n`;
        }
      } catch (e) {
        console.error('Error parseando knowledge base:', e);
      }
    }

    prompt += '\n';
  });

  return prompt.trim();
};

/**
 * Actualiza el campo productos en agent_config con el prompt generado
 */
const updateAgentConfigProducts = async (flowId: string, agentId?: string | null): Promise<void> => {
  console.log('🔧 [updateAgentConfigProducts] Iniciando actualización de productos...');
  console.log('🔧 [updateAgentConfigProducts] flowId:', flowId);
  console.log('🔧 [updateAgentConfigProducts] agentId recibido:', agentId);

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.error('❌ [updateAgentConfigProducts] Usuario no autenticado');
    throw new Error('Usuario no autenticado');
  }
  console.log('✅ [updateAgentConfigProducts] user_id:', user.id);

  // Generar el prompt de productos
  const productosPrompt = await generateProductsPrompt(flowId);
  console.log('📝 [updateAgentConfigProducts] Prompt generado:', productosPrompt.substring(0, 100) + '...');

  // Si ya tenemos agentId, usarlo directamente
  let targetAgentId = agentId;
  console.log('🎯 [updateAgentConfigProducts] targetAgentId inicial:', targetAgentId);

  if (!targetAgentId) {
    console.log('⚠️ [updateAgentConfigProducts] No se recibió agentId, buscando desde flow...');
    // Fallback: buscar desde el flow
    const { data: flowData, error: flowError } = await supabase
      .from('agent_training_flows')
      .select('agent_id')
      .eq('id', flowId)
      .maybeSingle();

    console.log('🔍 [updateAgentConfigProducts] flowData:', flowData);
    if (flowError) console.error('❌ [updateAgentConfigProducts] Error buscando flow:', flowError);

    targetAgentId = flowData?.agent_id;
    console.log('🎯 [updateAgentConfigProducts] targetAgentId desde flow:', targetAgentId);
  }

  if (!targetAgentId) {
    console.warn('⚠️ [updateAgentConfigProducts] No se encontró targetAgentId - No se actualizará agent_config');
    return;
  }

  console.log('💾 [updateAgentConfigProducts] Actualizando agent_config...');
  console.log('💾 [updateAgentConfigProducts] Condiciones: user_id =', user.id, ', agent_id =', targetAgentId);

  // Actualizar el campo productos en agent_config
  const { data: updateData, error } = await supabase
    .from('agent_config')
    .update({ productos: productosPrompt })
    .eq('user_id', user.id)
    .eq('agent_id', targetAgentId)
    .select();

  if (error) {
    console.error('❌ [updateAgentConfigProducts] Error actualizando:', error);
    console.debug('Info: No se pudo actualizar agent_config.productos:', error.message);
  } else {
    console.log('✅ [updateAgentConfigProducts] Actualización exitosa!');
    console.log('✅ [updateAgentConfigProducts] Datos actualizados:', updateData);
  }
};

export const agentProductsApi = {
  async getProductsByFlowId(flowId: string): Promise<AgentProduct[]> {
    const { data, error } = await supabase
      .from('agent_products')
      .select('*')
      .eq('flow_id', flowId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async createProduct(product: Omit<AgentProduct, 'id' | 'created_at' | 'updated_at'>, agentId?: string | null): Promise<AgentProduct> {
    console.log('➕ [createProduct] Creando producto...');
    console.log('➕ [createProduct] product:', product);
    console.log('➕ [createProduct] agentId:', agentId);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const { data, error } = await supabase
      .from('agent_products')
      .insert({ ...product, user_id: user.id })
      .select()
      .single();

    if (error) {
      console.error('❌ [createProduct] Error creando producto:', error);
      throw error;
    }

    console.log('✅ [createProduct] Producto creado:', data);

    // Actualizar el prompt de productos en agent_config
    try {
      console.log('🔄 [createProduct] Llamando a updateAgentConfigProducts...');
      await updateAgentConfigProducts(product.flow_id, agentId);
    } catch (e) {
      console.error('❌ [createProduct] Error actualizando prompt de productos:', e);
    }

    return data;
  },

  async updateProduct(id: string, product: Partial<AgentProduct>, agentId?: string | null): Promise<AgentProduct> {
    const { data, error } = await supabase
      .from('agent_products')
      .update(product)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Obtener el flow_id del producto para actualizar el prompt
    if (data?.flow_id) {
      try {
        await updateAgentConfigProducts(data.flow_id, agentId);
      } catch (e) {
        console.error('Error actualizando prompt de productos:', e);
      }
    }

    return data;
  },

  async deleteProduct(id: string, agentId?: string | null): Promise<void> {
    // Primero obtener el flow_id antes de eliminar
    const { data: productData } = await supabase
      .from('agent_products')
      .select('flow_id')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('agent_products')
      .delete()
      .eq('id', id);

    if (error) throw error;

    // Regenerar el prompt después de eliminar
    if (productData?.flow_id) {
      try {
        await updateAgentConfigProducts(productData.flow_id, agentId);
      } catch (e) {
        console.error('Error actualizando prompt de productos:', e);
      }
    }
  },

  /**
   * Obtiene el prompt formateado de productos para un flow específico
   */
  async getProductsPrompt(flowId: string): Promise<string> {
    return await generateProductsPrompt(flowId);
  }
};
